import { Contact } from '@interfaces/dws/contact';
import { Winery } from '@interfaces/dws/winery';
import { Country } from '@interfaces/dws/country';
import { Channel } from '@interfaces/dws/channel';
import { Product } from '@interfaces/dws/product';
import { OrderStatus } from 'interfaces/dws/order-status';
import { Address } from './address';
import { DWS } from '.';
import { PaymentMethod } from '@interfaces/dws/payment-method';

export namespace Order {
  export type FiscalDocumentKind =
    | "PENDING"
    | "RECEIPT_TRANSMITTED"
    | "RECEIPT_PARTIAL_CORRECTION"
    | "RECEIPT_TOTAL_CORRECTION"
    | "CANCELLATION"
    | "TRANSMISSION_FAILED";

  export interface FiscalDocumentSummary {
    kind: FiscalDocumentKind;
    viewDocumentId?: string;
    adeDocumentNumber?: string;
    transmittedAt?: string;
    correctionAt?: string;
    cancellationAt?: string;
    errorReason?: string;
  }

  export interface Data {
    amountDiscountCents?: number;
    amountShipmentCents?: number;
    firstName?: string;
    lastName?: string;
    companyName?: string;
    id?: number;
    code: string;
    quantity: number;
    amountCents: number;
    /** Paid total from order_totals.total_cents (GET /orders). */
    totalCents?: number;
    /** Gross product subtotal from order_totals.subtotal_cents (GET /orders). */
    subtotalCents?: number;
    contact: Contact;
    channel: Channel;
    country: Country;
    winery: Winery;
    contactId?: number;
    data: any;
    createdAt: string;
    updatedAt?: string;
    products: Product.InOrder[];
    address?: string;
    city?: string;
    zipcode?: string;
    paymentType: { id: string };
    province?: string;
    status: OrderStatus;
    sequence: number;
    date?: string;
    shippingName?: string;
    shippingPhone?: string;
    wineryChannelId?: string;
    wineryChannelName?: string;
    contactCountryName?: string;
    channelNameIt?: string;
    channelNameEn?: string;
    couponCode?: string;
    couponType?: string;
    couponDiscountCents?: number;
    couponDiscountPercent?: number;
    amountExciseDutyCents?: number;
    amountVatCents?: number;
    amountVatPercentage?: number;
    billingName?: string;
    billingAddress?: string;
    billingCity?: string;
    billingProvince?: string;
    billingZipcode?: string;
    billingCountry?: Country;
    addresses: DWS.Address[];
    notes?: string
    amountCentsDiscounted?: number;
    pointOfSaleWineSuiteId?: string;
    pointOfSaleWineSuiteName?: string;
    orderType?: string;
    /** Enriched client-side for Fiscality wineries (not from GET /orders). */
    fiscalDocumentSummary?: FiscalDocumentSummary;
    /** Fiscal storno (CANCELLATION) document number from vw_orders. */
    cancellationInternalDocumentNumber?: string;
    /** Display currency from line products (first non-EUR, else EUR). */
    currency?: string;
  }

  export interface New {
    id?: any;
    address?: string;
    amountCents: number;
    amountDiscountCents: number;
    amountShipmentCents: number;
    city?: string;
    code: any;
    contact: { id: string } | undefined;
    country?: { id: number };
    notes?: string;
    paymentType: { id: string };
    province: string;
    products: {
      amountCents: number;
      amountDiscountCents: number;
      barcode?: any;
      descriptionIt: string;
      descriptionEn?: string;
      nameIt: string;
      nameEn?: string;
      product: { id: any };
      quantity: number;
      type: { id: string };
    }[];
    sendEmail: boolean;
    senderEmail: string;
    createReceipt?: boolean;
    couponCode?: string;
    couponType?: string;
    couponDiscountCents?: number;
    couponDiscountPercent?: number;
  }

  export interface Filter {
    code?: string;
    id?: string;
    name?: string;
    createdAt?: string;
    updatedAt?: string;
    country?: Country;
    quantity?: string;
    amount?: any;
    wineryChannelName?: string;
    paymentType?: PaymentMethod;
    status?: OrderStatus;
    orderType?: string;
    startDate?: string;
    endDate?: string;
    orderDateStart?: string;
    orderDateEnd?: string;
    registrationStartDate?: string;
    registrationEndDate?: string;
    lastEditStartDate?: string;
    lastEditEndDate?: string;

    dateRangeOrderDate?: {
      start?: string;
      end?: string;
    };

    dateRangeCreation?: {
      start?: string;
      end?: string;
    };

    dateRangeUpdate?: {
      start?: string;
      end?: string;
    };
    pointOfSaleWineSuiteId?: string;
    fiscalDocumentFilter?: string;
  }

  export interface RequestFilter {
    code?: string;
    id?: string;
    /** Comma-separated order ids (shipping Da seguire export). */
    ids?: string;
    name?: string;
    /** WS-4274 search bar: order ID fragment, Rif., customer name or email. */
    q?: string;
    sortBy?: string;
    createdAt?: string;
    updatedAt?: string;
    country?: Country;
    quantity?: string;
    status?: OrderStatus;
    amountLte?: number;
    amountGte?: number;
    wineryChannelName?: string;
    paymentType?: PaymentMethod;
    wineryId: string
    contactId?: number;
    wineryChannelId?: string;
    startDate?: string;
    endDate?: string;
    orderDateStart?: string;
    orderDateEnd?: string;
    registrationStartDate?: string;
    registrationEndDate?: string;
    lastEditStartDate?: string;
    lastEditEndDate?: string;

    dateRangeCreation?: {
      start?: string;
      end?: string;
    };

    dateRangeUpdate?: {
      start?: string;
      end?: string;
    };
    pointOfSaleWineSuiteId?: string;
    /** Origin filter: "ecommerce" | "POS" | "self order". */
    orderType?: string;
    fiscalDocumentFilter?: string;
  }

  export interface Forecast {
    id: string;
    date: string;
    quantity: number;
    amountCents: number;
    wineryId: string;
    wineryChannelId: string;
  }

  export const channelNames = ['unknown', 'woocommerce', 'winery']

  export function isEcommerceOrderType(order: CrmOrder): boolean {
    const type = String(order?.orderType || '').toLowerCase();
    if (!type) {
      return true;
    }
    return !type.includes('pos') && !type.includes('self') && !type.includes('preorder');
  }

  export function getFullNameFromOrder(order: CrmOrder): string {
    // Ecommerce (WS-3837 r.3): the customer is always the buyer; the recipient only appears in the shipping address.
    if (!isEcommerceOrderType(order)) {
      // T-6: prefer order shipping recipient over contact first/last (can be overwritten on contact).
      const shippingName = (order.shippingName ?? (order as any).shipping_name)?.trim();
      if (shippingName) {
        return shippingName;
      }
    }
    const name =
      order.companyName ??
      ((order.firstName ?? "") + " " + (order.lastName ?? "")).trim();
    return name || "Guest";
  }
}

export type CrmOrder = Order.Data;
export type ForecastedOrder = Order.Forecast;